import {
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type MouseEvent as ReactMouseEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';

interface InfoTooltipProps {
  text: string;
  size?: number;
  position?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * Eigenes Symbol statt des Fragezeichens — für Fälle, in denen das Symbol selbst schon eine
   * Aussage trägt (etwa ein Warndreieck bei fehlendem Bestand). Ohne Angabe bleibt alles wie
   * bisher, damit die vorhandenen Verwendungen unverändert aussehen.
   */
  icon?: ReactNode;
  /**
   * `true`, wenn der Anker selbst Nachfahre eines `<a>`/`<Link>` ist — etwa in einer klickbaren
   * Dashboard-Kachel (TICK-2026-01121). Das HTML-Inhaltsmodell von `<a>` verbietet fokussierbare
   * Nachfahren: Ein zweiter `tabindex` innerhalb des Links ist ungültiges HTML, und auf einem
   * Touch-Gerät (kein Hover) löste ein Tipp auf das Fragezeichen bislang die Navigation des Links
   * aus statt den Hinweis zu zeigen. Mit `inLink` verzichtet der Anker auf einen eigenen
   * Tab-Stopp (die Kachel bleibt EIN Tab-Stopp) und reicht Klick/Tipp nicht an den Link durch —
   * bedienbar bleiben so beide: der Link per Klick auf den Rest der Kachel, der Hinweis per Klick
   * auf das Fragezeichen selbst.
   */
  inLink?: boolean;
}

const ABSTAND = 8;

/**
 * Hinweistext am Symbol.
 *
 * 🚨 **Der Text hängt an `document.body`, nicht an der Zelle.** Als absolut positioniertes Kind
 * wurde er von jedem Eltern-Element mit `overflow` beschnitten — in der Auftragsliste war nur ein
 * schmaler Streifen zu sehen („1000: 24 von 50 verfü…", Ben 15.08.2026). Tabellen scrollen
 * horizontal, Karten und Dialoge schneiden ab; ein Tooltip, den man nur an manchen Stellen lesen
 * kann, ist keiner. Über ein Portal mit `position: fixed` kann kein Container ihn mehr abschneiden.
 *
 * Die Position wird beim Öffnen aus dem Anker berechnet und am Fenster gehalten — ein Tooltip am
 * rechten Rand rutscht so nach innen, statt aus dem Bild zu laufen.
 */
export function InfoTooltip({ text, size = 13, position = 'top', icon, inLink = false }: InfoTooltipProps) {
  const ankerRef = useRef<HTMLSpanElement>(null);
  const hinweisRef = useRef<HTMLSpanElement>(null);
  const [offen, setOffen] = useState(false);
  const [koordinaten, setKoordinaten] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!offen || !ankerRef.current) {
      return;
    }

    const anker = ankerRef.current.getBoundingClientRect();
    const breite = 256; // w-64
    // Echte Höhe, sobald der Hinweis im Portal steht — beim ersten Durchlauf ist er noch nicht
    // gerendert, dann dient die Schätzung als Startwert und der zweite Durchlauf korrigiert.
    // Eine geschätzte Höhe allein reicht nicht: Bei acht Artikelnummern wären es schnell 200 px,
    // und der Hinweis stünde über dem Fensterrand.
    const geschaetzteHoehe = hinweisRef.current?.offsetHeight ?? 40 + Math.floor(text.length / 40) * 18;

    let top: number;
    let left: number;

    switch (position) {
      case 'bottom':
        top = anker.bottom + ABSTAND;
        left = anker.left + anker.width / 2 - breite / 2;
        break;
      case 'left':
        top = anker.top + anker.height / 2 - geschaetzteHoehe / 2;
        left = anker.left - breite - ABSTAND;
        break;
      case 'right':
        top = anker.top + anker.height / 2 - geschaetzteHoehe / 2;
        left = anker.right + ABSTAND;
        break;
      default:
        top = anker.top - geschaetzteHoehe - ABSTAND;
        left = anker.left + anker.width / 2 - breite / 2;
    }

    // Am Fenster halten: lieber verschoben als abgeschnitten.
    left = Math.min(Math.max(ABSTAND, left), window.innerWidth - breite - ABSTAND);
    top = Math.max(ABSTAND, top);

    setKoordinaten((vorher) =>
      vorher && vorher.top === top && vorher.left === left ? vorher : { top, left }
    );
  }, [offen, position, text, koordinaten]);

  // 🚨 **Nur innerhalb eines Links noetig, deshalb an `inLink` gebunden** (TICK-2026-01121). Ein
  // Klick/Tipp auf das Fragezeichen reichte bislang zum umgebenden `<Link>` durch, der dann
  // navigierte statt den Hinweis zu zeigen — auf Touch gibt es kein Hover, das war dort der
  // einzige Ausloeser. Ausserhalb eines Links bleibt das Verhalten UNVERAENDERT (Hover/Fokus wie
  // bisher): ein zusaetzlicher Klick-Handler an jeder der uebrigen Verwendungen im Projekt koennte
  // mit einem eigenen `onClick` der umgebenden Zeile/Karte kollidieren, ohne dass hier ein
  // Touch-Problem gemeldet ist.
  const handleClick = inLink
    ? (event: ReactMouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setOffen((vorher) => !vorher);
      }
    : undefined;
  const handleKeyDown = inLink
    ? (event: ReactKeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          setOffen((vorher) => !vorher);
        }
      }
    : undefined;

  return (
    <>
      <span
        ref={ankerRef}
        className="relative inline-flex cursor-help"
        onMouseEnter={() => setOffen(true)}
        onMouseLeave={() => setOffen(false)}
        onFocus={() => setOffen(true)}
        onBlur={() => setOffen(false)}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        // 🚨 Ohne `inLink` wie bisher ein eigener Tab-Stopp. MIT `inLink` verbietet das
        // HTML-Inhaltsmodell von `<a>` einen fokussierbaren Nachfahren — die Kachel bleibt dann
        // EIN Tab-Stopp, bedienbar wird der Hinweis dort ueber Maus/Touch (Klick) statt Tastatur.
        tabIndex={inLink ? undefined : 0}
      >
        {icon ?? (
          <HelpCircle size={size} className="opacity-50 hover:opacity-80 transition-opacity" />
        )}
      </span>
      {offen &&
        koordinaten &&
        createPortal(
          <span
            ref={hinweisRef}
            className="fixed w-64 p-2.5 rounded-lg bg-slate-700 text-slate-200 text-xs leading-relaxed whitespace-pre-line shadow-xl z-[9999] pointer-events-none"
            style={{ top: koordinaten.top, left: koordinaten.left }}
          >
            {text}
          </span>,
          document.body
        )}
    </>
  );
}
